// Deployment trigger: Ensure production has mobile menu hiding for side menu
import type { AppProps } from "next/app"
import { SessionProvider, useSession } from "next-auth/react"
import React from "react"
import Head from "next/head"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import { CssBaseline, Box } from "@mui/material"
import { useRouter } from "next/router"

import darkTheme from "@/theme/darkTheme"
import lightTheme from "@/theme/lightTheme"
import teslaLightTheme from "@/theme/teslaLightTheme"
import teslaDarkTheme from "@/theme/teslaDarkTheme"
import type { ThemeStyle } from "@/hooks/useUserPreferences"
import Header from "@/components/layout/header"
import SideMenu from "@/components/SideMenu"
import { MobileBottomNav } from "@/components/MobileBottomNav"
import { LANDSCAPE_HEADER_HEIGHT, LANDSCAPE_PHONE_QUERY, LANDSCAPE_RAIL_WIDTH, landscapePhone } from "@/utils/landscapePhone"
import { useUserPreferences } from "@/hooks/useUserPreferences"
import NotYetAvailable from "./features/notYetAvailable"
import { ChangelogDialog } from "@/components/Changelog"
import { CHANGELOG_STORAGE_KEY, CHANGELOG_VERSION } from "@/constants/changelog"
import { LanguageProvider } from "@/contexts/LanguageContext"
import { FirestoreMissingIndexProvider } from "@/contexts/FirestoreMissingIndexContext"
import { PropertySearchStatsProvider } from "@/contexts/PropertySearchStatsContext"
import "leaflet/dist/leaflet.css"

export const ColorModeContext = React.createContext({ toggleColorMode: () => {} })

function AppContent({ Component, pageProps }: Pick<AppProps, "Component" | "pageProps">) {
  const [mode, setMode] = React.useState<"light" | "dark">("light")
  const [isChangelogOpen, setIsChangelogOpen] = React.useState(false)
  const { data: session } = useSession()
  const router = useRouter()
  const { preferences, refetch: refetchPreferences } = useUserPreferences()
  const isDashboard = router.pathname === "/features" || router.asPath === "/#" || router.asPath === "/"
  const menuLayout = preferences?.menuLayout || "side"
  const isDashboardRoute = isDashboard
  const headerHeight = session ? (menuLayout === "horizontal" ? 69 : 64) : 0
  const mobileBottomNavHeight = 64 // Height of mobile bottom navigation
  const fullHeightRoute = `calc(100vh - ${headerHeight}px)`

  // Routes that need full viewport height (no page-level scrollbar)
  const fullHeightRoutes = [
    "/features",
    "/features/search",
    "/features/hot-deals",
    "/features/propertyMap",
    "/features/search/favorites",
    "/features/chat",
    "/features/building-simulator",
    "/features/search/apartments-explorer",
    "/features/search/houses-explorer"
  ]
  // Match '/features' only for the dashboard; other /features/* use scrollable layout unless listed
  const isFullHeightRoute = fullHeightRoutes.some((route) =>
    route === "/features" ? router.pathname === "/features" : router.pathname === route || router.asPath.startsWith(route)
  )

  // Listen for preferences updates
  React.useEffect(() => {
    const handlePreferencesUpdate = () => {
      refetchPreferences()
    }
    if (typeof window !== "undefined") {
      window.addEventListener("preferencesUpdated", handlePreferencesUpdate)
      return () => {
        window.removeEventListener("preferencesUpdated", handlePreferencesUpdate)
      }
    }
  }, [refetchPreferences])

  const colorMode = React.useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => (prevMode === "light" ? "dark" : "light"))
      }
    }),
    []
  )

  const themeStyle: ThemeStyle = preferences?.themeStyle === "legacy" ? "legacy" : "tesla"
  const theme = React.useMemo(() => {
    if (themeStyle === "legacy") {
      return createTheme(mode === "dark" ? darkTheme : lightTheme)
    }
    return createTheme(mode === "dark" ? teslaDarkTheme : teslaLightTheme)
  }, [mode, themeStyle])

  const isOdynxEmail = session?.user?.email?.endsWith("@odynx.com")
  const hasWhitelistedGmailAccess = session?.user?.isWhitelistedGmail

  React.useEffect(() => {
    if (!session) {
      return
    }
    if (typeof window === "undefined") {
      return
    }
    const storedVersion = window.localStorage.getItem(CHANGELOG_STORAGE_KEY)
    if (storedVersion !== CHANGELOG_VERSION) {
      setIsChangelogOpen(true)
    }
  }, [session])

  const handleCloseChangelog = React.useCallback(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(CHANGELOG_STORAGE_KEY, CHANGELOG_VERSION)
    }
    setIsChangelogOpen(false)
  }, [])

  if (session && !isOdynxEmail && !hasWhitelistedGmailAccess) {
    return <NotYetAvailable />
  }

  return (
    <ColorModeContext.Provider value={colorMode}>
      <Head>
        {/* Lowest-priority fallback - any page (or SearchPageContent) rendering its own
            <title> via next/head overrides this, per Next.js's "last title wins" rule. */}
        <title>ODYNX</title>
      </Head>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            // Phones: exactly the visible viewport (`dvh` follows the browser's collapsing
            // address bar, `vh` doesn't) and clipped, so the document itself can never scroll -
            // only the page's inner box does, which keeps the fixed bottom nav truly pinned.
            height: { xs: "100dvh", md: "auto" },
            minHeight: { md: "100vh" },
            // Phone held sideways: same locked viewport as a portrait phone, even when wider than `md`.
            ...landscapePhone({ height: "100dvh", minHeight: 0 }),
            position: "relative",
            overflow: "hidden",
            background: "transparent",
            ...(themeStyle === "legacy" && {
              "&::before": {
                content: '""',
                position: "fixed",
                width: "65vw",
                height: "65vw",
                top: "-20vw",
                right: "-25vw",
                background:
                  "radial-gradient(circle at center, rgba(148, 197, 255, 0.55) 0%, rgba(244, 247, 255, 0.1) 65%, rgba(244, 247, 255, 0) 100%)",
                filter: "blur(75px)",
                opacity: theme.palette.mode === "dark" ? 0.35 : 0.6,
                zIndex: 0,
                pointerEvents: "none"
              },
              "&::after": {
                content: '""',
                position: "fixed",
                width: "55vw",
                height: "55vw",
                bottom: "-30vw",
                left: "-20vw",
                background:
                  "radial-gradient(circle at center, rgba(200, 160, 255, 0.45) 0%, rgba(244, 245, 255, 0.12) 60%, rgba(244, 245, 255, 0) 100%)",
                filter: "blur(90px)",
                opacity: theme.palette.mode === "dark" ? 0.3 : 0.55,
                zIndex: 0,
                pointerEvents: "none"
              }
            })
          }}>
          {session && <Header />}
          <Box sx={{ display: "flex", flex: 1 }}>
            {session && !isDashboard && menuLayout === "side" && (
              <Box sx={{ display: { xs: "none", md: "block" }, ...landscapePhone({ display: "none" }) }}>
                <SideMenu />
              </Box>
            )}
            <Box
              component="main"
              sx={{
                flexGrow: 1,
                // A flex item defaults to `min-width: auto`, i.e. it refuses to shrink below its
                // content. Any wide child (a scrollable tab bar, a diagram, a table) then stretches
                // <main> past the viewport and the page gets clipped on the right on phones.
                minWidth: 0,
                p: isDashboardRoute || isFullHeightRoute ? 0 : { xs: 0.5, md: 1 },
                mt: session ? (menuLayout === "horizontal" ? "69px" : "64px") : 0,
                mb: { xs: session ? `${mobileBottomNavHeight}px` : 0, md: 0 },
                position: "relative",
                zIndex: 1,
                ...(isFullHeightRoute && {
                  height: { xs: `calc(100dvh - ${headerHeight}px - ${mobileBottomNavHeight}px)`, md: fullHeightRoute },
                  overflow: "hidden"
                }),
                // The phone document is locked (see the global style below), so the dashboard - the
                // one route that renders without the scrolling page box - scrolls inside <main>.
                ...(isDashboardRoute &&
                  !isFullHeightRoute && {
                    height: { xs: `calc(100dvh - ${headerHeight}px - ${session ? mobileBottomNavHeight : 0}px)`, md: "auto" },
                    overflowY: { xs: "auto", md: "visible" }
                  }),
                // Phone held sideways: slim header on top, nav rail on the left, no bottom nav.
                ...landscapePhone({
                  mt: session ? `${LANDSCAPE_HEADER_HEIGHT}px` : 0,
                  mb: 0,
                  ml: session ? `${LANDSCAPE_RAIL_WIDTH}px` : 0,
                  ...((isFullHeightRoute || isDashboardRoute) && {
                    height: `calc(100dvh - ${session ? LANDSCAPE_HEADER_HEIGHT : 0}px)`,
                    overflow: isFullHeightRoute ? "hidden" : "auto"
                  })
                })
              }}>
              {isDashboardRoute || isFullHeightRoute ? (
                <Component {...pageProps} />
              ) : (
                <Box
                  sx={(themeInner) => {
                    const isTesla = themeStyle === "tesla"
                    return {
                      position: "relative",
                      zIndex: 1,
                      borderRadius: { xs: 3, md: 6 },
                      overflowX: "hidden",
                      overflowY: "auto",
                      ...(isTesla
                        ? {
                            backgroundColor: themeInner.palette.background.paper,
                            border: `1px solid ${themeInner.palette.divider}`,
                            boxShadow: "none"
                          }
                        : {
                            backgroundColor: themeInner.palette.mode === "dark" ? "rgba(15, 23, 42, 0.58)" : "rgba(255, 255, 255, 0.55)",
                            backdropFilter: "saturate(180%) blur(28px)",
                            border:
                              themeInner.palette.mode === "dark"
                                ? "1px solid rgba(148, 163, 184, 0.24)"
                                : "1px solid rgba(255, 255, 255, 0.42)",
                            boxShadow:
                              themeInner.palette.mode === "dark"
                                ? "0 30px 60px -48px rgba(15, 23, 42, 0.85)"
                                : "0 30px 65px -50px rgba(15, 23, 42, 0.35)"
                          }),
                      // Phones: viewport minus header (main's top margin), bottom nav (its bottom
                      // margin) and main's own 4px+4px padding, so shell + page fit the screen
                      // exactly - the old `100vh - 120px` overshot by ~16px and let the whole page scroll.
                      minHeight: {
                        xs: `calc(100dvh - ${headerHeight}px - ${session ? mobileBottomNavHeight : 0}px - 8px)`,
                        md: "calc(100vh - 120px)"
                      },
                      maxHeight: {
                        xs: `calc(100dvh - ${headerHeight}px - ${session ? mobileBottomNavHeight : 0}px - 8px)`,
                        md: "calc(100vh - 120px)"
                      },
                      px: { xs: 0.5, md: 1 },
                      py: { xs: 1, md: 1.5 },
                      ...landscapePhone({
                        minHeight: `calc(100dvh - ${session ? LANDSCAPE_HEADER_HEIGHT : 0}px - 8px)`,
                        maxHeight: `calc(100dvh - ${session ? LANDSCAPE_HEADER_HEIGHT : 0}px - 8px)`,
                        borderRadius: 3,
                        px: 0.5,
                        py: 1
                      })
                    }
                  }}>
                  <Component {...pageProps} />
                </Box>
              )}
            </Box>
          </Box>
          {session && (
            <>
              <ChangelogDialog open={Boolean(session && isChangelogOpen)} onClose={handleCloseChangelog} />
              <MobileBottomNav />
            </>
          )}
        </Box>
      </ThemeProvider>
      <style jsx global>{`
        /* Phones: the document never scrolls (in either axis) - the header and bottom nav stay put and
           only the page's own box scrolls. Fixed + inset 0 also stops iOS rubber-banding the page. */
        @media (max-width: 899.95px), ${LANDSCAPE_PHONE_QUERY} {
          html,
          body {
            height: 100%;
            overflow: hidden;
            overscroll-behavior: none;
          }
          body {
            position: fixed;
            inset: 0;
            width: 100%;
          }
        }
        /* Phone held sideways (see utils/landscapePhone.ts): ~390px of height, so every overlay has to fit it.
           Global because dialogs, menus and popovers render in portals, outside any sx we control. */
        @media ${LANDSCAPE_PHONE_QUERY} {
          .MuiDialog-paper:not(.MuiDialog-paperFullScreen) {
            margin: 8px !important;
            max-height: calc(100% - 16px) !important;
          }
          .MuiDialogTitle-root {
            padding-top: 8px !important;
            padding-bottom: 8px !important;
          }
          .MuiDialogContent-root {
            padding-top: 8px !important;
            padding-bottom: 8px !important;
          }
          .MuiDialogActions-root {
            padding: 6px 12px !important;
          }
          .MuiPopover-paper,
          .MuiMenu-paper {
            max-height: calc(100dvh - 16px) !important;
          }
          .MuiAutocomplete-listbox {
            max-height: 42dvh !important;
          }
          .MuiTab-root {
            min-height: 40px !important;
          }
          .MuiTabs-root {
            min-height: 40px !important;
          }
        }
        @keyframes gradientMove {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }
      `}</style>
    </ColorModeContext.Provider>
  )
}

export default function App({ Component, pageProps }: AppProps) {
  return (
    <SessionProvider session={pageProps.session}>
      <LanguageProvider>
        <FirestoreMissingIndexProvider>
          <PropertySearchStatsProvider>
            <AppContent Component={Component} pageProps={pageProps} />
          </PropertySearchStatsProvider>
        </FirestoreMissingIndexProvider>
      </LanguageProvider>
    </SessionProvider>
  )
}
